<template>
  <div data-test-id="MediaButtonComponent">
    <div
      class="media-button btn btn-secondary border border-dark rounded d-flex align-items-center p-2"
      :data-test-id="`${eventType}MediaButtonComponent`"
      @click="showModal"
    >
      <b-icon :icon="icon" class="m-2"></b-icon>

      <div class="d-flex flex-column flex-fill text-left ml-2">
        <strong>{{ label }}</strong>

        <span v-if="description" class="w-100 small font-weight-normal" data-test-id="MediaButtonDescription">
          {{ description }}
        </span>
      </div>

      <input type="text" disabled :value="count" :class="{ red: count > 0 }" class="m-2" size="2" />
    </div>

    <b-modal ref="media-modal" hide-footer :data-event-type="eventType" data-test-id="media-modal">
      <template #modal-title>
        <h5 class="modal-title" :style="modalTitleStyle">{{ modalTitle }}</h5>
      </template>

      <div class="d-block text-center message-center" :style="modalContentStyle">
        <Camera
          v-if="eventType === CaseUpdateEventType.photo || eventType === CaseUpdateEventType.id"
          :event-type="eventType"
          @modify-case="onModifyCase"
          @hide-modal="hideModal"
        />
        <Ekg
          v-if="eventType === CaseUpdateEventType.ekg"
          :event-type="eventType"
          :monitors="monitors"
          :form-data="formData"
          @update-modal="onUpdateModal"
          @modify-case="onModifyCase"
          @hide-modal="hideModal"
        />
        <Video
          v-if="eventType === CaseUpdateEventType.video"
          :event-type="eventType"
          @modify-case="onVideoEvent"
          @hide-modal="hideModal"
        />
        <VoiceMemo
          v-if="eventType === CaseUpdateEventType.voiceMemo"
          :event-type="eventType"
          @modify-case="onVoiceMemoEvent"
          @hide-modal="hideModal"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BIcon } from "bootstrap-vue";

import Camera from "@/Case/components/Camera.vue";
import VoiceMemo from "@/Case/components/VoiceMemo.vue";
import Video from "@/Case/components/Video.vue";
import { CaseEvent, CaseUpdateEventType } from "@/Case/CaseEvent";
import Ekg from "@/Case/components/Ekg";

export default {
  name: "MediaButton",
  // eslint-disable-next-line vue/no-reserved-component-names
  components: { Ekg, BIcon, Camera, VoiceMemo, Video },
  props: {
    label: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    count: {
      type: Number,
      default: 0,
    },
    eventType: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    modalContentStyle: {
      type: Object,
      default: () => ({}),
    },
    monitors: {
      type: Array,
      default: () => [],
    },
    formData: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      worker: undefined,
      modalTitle: this.label,
      modalTitleStyle: "",
    };
  },
  computed: {
    CaseUpdateEventType() {
      return CaseUpdateEventType;
    },
  },
  mounted() {
    this.setupWorker();
  },
  destroyed() {
    if (this.worker) {
      this.worker.terminate();
    }
  },
  methods: {
    showModal() {
      this.$refs["media-modal"].show();
    },
    hideModal() {
      this.$refs["media-modal"].hide();
    },
    onModifyCase(event) {
      this.hideModal();
      this.$emit(CaseEvent.MODIFY, event);
    },
    onVideoEvent(event) {
      this.hideModal();

      if (this.worker && event.eventType === CaseUpdateEventType.video) {
        const reader = new FileReader();

        reader.onload = (evt) => {
          this.worker.postMessage({
            type: "run",
            MEMFS: [
              {
                name: `original.webm`,
                data: evt.target.result,
              },
            ],
            arguments: [
              "-i",
              "original.webm",
              "-vcodec",
              "libx264",
              "-c:a",
              "aac",
              "-pix_fmt",
              "yuv420p",
              "-preset",
              "ultrafast",
              "converted.mp4",
            ],
          });
        };

        reader.readAsArrayBuffer(event.data);
      } else {
        this.$emit(CaseEvent.MODIFY, event);
      }
    },
    onVoiceMemoEvent(event) {
      this.hideModal();

      if (this.worker && event.eventType === CaseUpdateEventType.voiceMemo) {
        const reader = new FileReader();

        reader.onload = (evt) => {
          this.worker.postMessage({
            type: "run",
            MEMFS: [
              {
                name: `original.webm`,
                data: evt.target.result,
              },
            ],
            arguments: ["-i", "original.webm", "converted.mp3"],
          });
        };

        reader.readAsArrayBuffer(event.data);
      } else {
        this.$emit(CaseEvent.MODIFY, event);
      }
    },
    setupWorker() {
      if ("Worker" in window) {
        this.worker = new Worker(`/ffmpeg-worker-mp4.js`);

        this.worker.onmessage = (e) => {
          const msg = e.data;
          switch (msg.type) {
            case "stdout":
            case "stderr":
              break;
            case "exit":
              break;
            case "done":
              if (this.eventType === CaseUpdateEventType.video) {
                this.$emit(CaseEvent.MODIFY, {
                  eventType: this.eventType,
                  id: `${this.eventType}.mp4`,
                  data: new Blob([msg.data.MEMFS[0].data], {
                    type: "video/mp4",
                  }),
                });
              } else if (this.eventType === CaseUpdateEventType.voiceMemo) {
                this.$emit(CaseEvent.MODIFY, {
                  eventType: this.eventType,
                  id: `${this.eventType}.mp3`,
                  data: new Blob([msg.data.MEMFS[0].data], {
                    type: "audio/mp3",
                  }),
                });
              }
              break;
          }
        };
      }
    },
    onUpdateModal({ title, titleStyle }) {
      if (title) {
        this.modalTitle = title;
      }
      if (titleStyle) {
        this.modalTitleStyle = titleStyle;
      }
    },
  },
};
</script>

<style scoped>
.message-center {
  min-height: 400px;
}
.media-button {
  background-color: white;
  color: black;
  min-height: 52px;
}
.media-button input {
  text-align: center;
  font-weight: bold;
}
.media-button input.red {
  background-color: rgb(191, 19, 17);
  color: #fff;
  border: 2px solid rgb(191, 19, 17);
}
@media (max-width: 375px) {
  .modal-title {
    font-size: 1.1rem;
  }
}
</style>
